import { debounce } from "lodash";
import { useEffect, useState } from "react";
import { IHDItemSearchResponseData } from "@/api_framework/api_modals/Cart";
import SimpleBadge from "@/app/components/ui/Badges/SimpleBadge";
import { Combobox, Transition } from "@headlessui/react";
import { ChevronUpDownIcon } from "@heroicons/react/20/solid";
import { MagnifyingGlassIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { useDebounce } from "@uidotdev/usehooks";
import clsx from "clsx";
import { Fragment } from "react";
import { useLoader } from "@/app/hooks/useLoader";
import CircleSpinnerLoader from "@/app/components/ui/Reusable/LoaderComponent/CircleSpinnerLoader";
import { BiRupee } from "react-icons/bi";
import { useHDCartSearchAPI } from "@/app/hooks/api_hooks/HDCart/useHDCartSearchAPI";
import { toast } from "react-hot-toast";
import { IClickLogBody, useClickLogAPI } from "@/app/hooks/api_hooks/org/useClickLogAPI";

const HDCartProductSearchComponent: React.FC<{
  containerClassName?: string;
  placeholder?: string;
  inputContainerClassName?: string;
  itemActiveClassName?: string;
  onSelectItem?: (item: IHDItemSearchResponseData) => void;
  pincode?: string;
  customerId?: string;
  petId?: string;
  mappedClinicID?: string;
  mappedClinicName?: string;
  userLogInfo?: string;
  cartItemsList?: any;
}> = ({
  containerClassName,
  inputContainerClassName,
  onSelectItem,
  pincode,
  customerId,
  petId,
  mappedClinicID,
  mappedClinicName,
  userLogInfo,
  cartItemsList,
  ...props
}) => {
  const { data: searchResults, execute: searchCartProducts, clearSearchResults } = useHDCartSearchAPI();
  const [selected, setSelected] = useState();
  const [isBarcodeScan, setIsBarcodeScan] = useState(false);
  const [lastInputTime, setLastInputTime] = useState<number | null>(null);
  const [query, setQuery] = useState("");
  const debouncedQuery = useDebounce(query?.trim(), 300);
  const isSearchPending = useLoader("isCustomerCartItemsSearchInProgress");
  const [activeTab, setActiveTab] = useState("All");
  const tabs = ["All", "Food", "Accessories", "Medicines"];
  const { execute: logAPI } = useClickLogAPI();

  const callLogAPI = (searchString: string, searchData?: any, clickItem?: any) => {
    const logBody: IClickLogBody = {
      event_type: clickItem ? "CART_CLICK" : searchData ? "CART_VIEW" : "CART_SEARCH",
      scr_id: "CART",
      client_id: customerId,
      pet_id: petId,
      search_item: searchString,
      search_tag: activeTab,
      search_result: searchData ?? undefined,
      click_on_search_item: clickItem ?? undefined,
      clinic_id: mappedClinicID,
      clinic_name: mappedClinicName,
      emr_cart_items: cartItemsList,
      vms_user_id: userLogInfo,
      cart_id: cartItemsList?.cart_id,
      timestamp: Date.now(),
    };
    logAPI(logBody);
  };

  const debounceSearch = debounce(() => {
    searchCartProducts(debouncedQuery, pincode, activeTab).then(({ status, message }) => {
      callLogAPI(debouncedQuery, status ? message : []);
    });
  }, 500);

  useEffect(() => {
    if (debouncedQuery.length >= 3) {
      debounceSearch();
    } else {
      clearSearchResults();
    }
  }, [debouncedQuery, activeTab]);

  // Address change invalidates results fetched for the previous pincode
  useEffect(() => {
    clearSearchResults();
    if (debouncedQuery.length >= 3) {
      debounceSearch();
    }
  }, [pincode]);

  const handleKeyPress = (event: React.KeyboardEvent<HTMLInputElement>) => {
    const now = Date.now();
    if (lastInputTime && now - lastInputTime < 50) {
      setIsBarcodeScan(true);
    } else {
      setIsBarcodeScan(false);
    }
    setLastInputTime(now);
    if (event.key === "Enter" && query.length >= 6) {
      setIsBarcodeScan(true);
    }
  };

  useEffect(() => {
    if (isBarcodeScan && searchResults?.length === 1) {
      onSelectItem(searchResults[0]);
      setQuery("");
      clearSearchResults();
    }
  }, [isBarcodeScan, searchResults]);

  useEffect(() => {
    if (query === "") {
      setActiveTab("All");
    }
  }, [query]);

  return (
    <div className={clsx(containerClassName)}>
      <Combobox
        value={selected}
        onChange={(item) => {
          if (item && query.trim().length >= 3) {
            setSelected(item);
            onSelectItem?.(item);
            callLogAPI(debouncedQuery, null, item);
          } else if (query.trim().length < 3) {
            toast.error("Please type at least 3 characters to add item.");
          }
        }}
      >
        <div className="relative mt-1 w-full">
          <div className="relative border-gray-200 bg-white border rounded-lg focus-visible:ring-2 focus-visible:ring-white/75 focus-visible:ring-offset-2 focus-visible:ring-offset-teal-300 w-full text-left sm:text-sm  overflow-hidden focus:outline-none">
            <div className="flex justify-start items-center">
              {query?.length >= 3 ? (
                <button className="p-2">
                  <XMarkIcon
                    className="w-4 h-4 text-red-500  cursor-pointer hover:text-red-800"
                    onClick={() => {
                      setQuery("");
                      clearSearchResults();
                      setIsBarcodeScan(false);
                    }}
                  />
                </button>
              ) : (
                <span className="p-2">
                  <MagnifyingGlassIcon className="w-4 h-4 text-gray-500" />
                </span>
              )}
              <Combobox.Button className={clsx(inputContainerClassName)}>
                <Combobox.Input
                  autoComplete="off"
                  autoCorrect="off"
                  autoFocus
                  value={query}
                  placeholder={props?.placeholder ?? "Start Searching ..."}
                  className="py-2 pr-10 pl-3 border-none focus:ring-0 w-full text-gray-900 text-sm leading-5"
                  onChange={(event) => setQuery(event.target.value)}
                  onBlur={() => onSelectItem(null)}
                  onKeyDown={handleKeyPress}
                />
              </Combobox.Button>
            </div>
            <Combobox.Button className="right-0 absolute inset-y-0 flex items-center pr-2">
              <ChevronUpDownIcon className="w-5 h-5 text-gray-400" aria-hidden="true" />
            </Combobox.Button>
          </div>
          <Transition as={Fragment} leave="transition ease-in duration-100" leaveFrom="opacity-100" leaveTo="opacity-0">
            <Combobox.Options className="z-20 absolute bg-white shadow-lg mt-1 rounded-md ring-1 ring-black/5 max-h-96 w-full text-base sm:text-sm focus:outline-none">
              <div className="max-h-72 overflow-y-auto">
                <div className="flex gap-3 m-3">
                  {tabs.map((tab) => (
                    <div
                      key={tab}
                      onClick={() => setActiveTab(tab)}
                      className={`cursor-pointer rounded-2xl py-1 px-5 border 
            ${activeTab === tab ? "bg-black text-white border-black" : "bg-[#f4f4f4] text-[#4d4f53] border-[#e2e2e2]"}`}
                    >
                      {tab}
                    </div>
                  ))}
                </div>
                {searchResults.length === 0 && query !== "" ? (
                  <div className="relative px-4 py-2 text-gray-700 cursor-default select-none">
                    {isSearchPending ? <CircleSpinnerLoader h="20" w="20" /> : <p>No Result Found.</p>}
                  </div>
                ) : (
                  <>
                    <div className="px-4 py-2 text-[#4d4f53]">
                      Search Results for <span className="text-[#0069f9] font-semibold">{debouncedQuery}</span>
                    </div>
                    {searchResults.map((result, idx) => (
                      <>
                        <Combobox.Option
                          key={`${result?.id}:${idx}`}
                          className={() => `relative cursor-default select-none p-4 text-gray-900 ml-1`}
                          value={result}
                        >
                          {({ selected }) => (
                            <div className="flex justify-between items-center gap-x-2">
                              <div className="w-[60%] flex justify-start items-center">
                                <span className={`block text-[13px] ${selected ? "font-medium" : "font-normal"}`}>
                                  {result.name?.split("_")?.join(" ")}
                                </span>
                              </div>
                              {Number(result?.unit_price) > 0 && (
                                <div className="flex items-center">
                                  <BiRupee />
                                  <div className="text-black text-[13px]">{result?.unit_price}</div>
                                </div>
                              )}
                              <div className="flex gap-x-3">
                                {result?.stock_qty_with_text &&
                                result?.stock_qty_with_text?.toLocaleLowerCase() === "unavailable" ? (
                                  <SimpleBadge
                                    colorClassName="text-[#b93b48] bg-[#fbdfe3] ring-[#fbdfe3]"
                                    label={result?.stock_qty_with_text}
                                  />
                                ) : (
                                  <div className="flex items-center gap-2">
                                    <div className="text-[#0069f9]">HD</div>
                                    <SimpleBadge
                                      colorClassName="text-[#006ecf] bg-[#edf5fe] ring-[#0069f9]"
                                      label={result?.stock_qty_with_text}
                                    />
                                  </div>
                                )}
                              </div>
                            </div>
                          )}
                        </Combobox.Option>
                      </>
                    ))}
                  </>
                )}
              </div>
              <div className="flex gap-2 m-3">
                <div className="bg-[#edf5fe] text-[#0e6cd1] rounded-md py-1 px-2 text-xs">Home Delivery</div>
                <div className="bg-[#fbdfe3] text-[#b93b48] rounded-md py-1 px-2 text-xs">Unavailable</div>
              </div>
            </Combobox.Options>
          </Transition>
        </div>
      </Combobox>
    </div>
  );
};

export default HDCartProductSearchComponent;
